import React, { FormEvent, useEffect, useState } from "react";
import {
	Button,
	Form,
	FormFeedback,
	FormGroup,
	Input,
	Label,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Spinner,
} from "reactstrap";
import { Dispatch } from "redux";
import { ManageCategoryAction } from "../../actions/manageCategoryAction";
import { showAlert } from "../common/functions";

interface ManageCategoryModalProps {
	isOpen: boolean;
	toggleDetailModal: () => void;
	id: number;
	dispatch: Dispatch<any>;
	manageCategoryData: any;
	detailLoading: any;
}

const ManageCategoryModal: React.FC<ManageCategoryModalProps> = ({
	isOpen = false,
	toggleDetailModal,
	id,
	dispatch,
	manageCategoryData,
	detailLoading,
}) => {
	const [categoryName, setCategoryName] = useState("");
	const [mediaType, setMediaType] = useState("video");
	const [description, setDescription] = useState("");
	const [categoryStatus, setCategoryStatus] = useState(true);
	const [isLoading, setLoading] = useState<boolean>(false);
	const [errors, setErrors] = useState<{
		categoryName?: string;
	}>({});

	const clearForm = () => {
		setCategoryName("");
		setMediaType("video");
		setDescription("");
		setCategoryStatus(true);
		setErrors({});
		setLoading(false);
	};

	useEffect(() => {
		setLoading(false);
	}, [detailLoading]);

	useEffect(() => {
		clearForm();
	}, [isOpen]);

	React.useEffect(() => {
		if (id > 0 && manageCategoryData?.items?.categories?.length > 0) {
			const selectedCategory = manageCategoryData.items.categories.find(
				(cat: any) => cat.categoryId === id
			);
			if (selectedCategory) {
				setCategoryName(selectedCategory.categoryName ?? "");
				setMediaType(selectedCategory.mediaTypeCode ?? "video");
				setDescription(selectedCategory.description ?? "");
				setCategoryStatus(selectedCategory.isActive ?? true);
			}
		} else {
			clearForm();
		}
	}, [id]);

	React.useEffect(() => {
		if (manageCategoryData.isSuccessSave === true) {
			clearForm();
			toggleDetailModal();
			setLoading(false);
		}
	}, [manageCategoryData.isSuccessSave]);

	const validate = () => {
		const newErrors: { categoryName?: string; description?: string } = {};

		if (!categoryName.trim()) {
			newErrors.categoryName = "Category name is required.";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();

		if (!validate()) return;
		setLoading(true);
		const payload = {
			categoryName,
			mediaTypeCode: mediaType,
			description,
			isActive: categoryStatus,
			...(id !== 0 && { categoryId: id }),
		};
		try {
			if (id === 0) {
				await dispatch(
					ManageCategoryAction.manageCategoryCreate({ categorie: payload })
				);
			} else {
				await dispatch(
					ManageCategoryAction.manageCategoryUpdate({ categorie: payload })
				);
			}
			// clearForm();
		} catch (error) {
			console.error("Error while saving:", error);
			showAlert("Failed to save category.", true);
		} finally {
			// setLoading(false);
			// toggleDetailModal();
		}
	};

	return (
		<Modal isOpen={isOpen} size="lg" centered backdrop={true}>
			<ModalHeader>
				{id > 0 ? "Manage Category Details" : "Add New Category"}
			</ModalHeader>
			<Form onSubmit={handleSubmit}>
				<ModalBody>
					<FormGroup>
						<Label for="categoryName" className="col-form-label">
							Category Name
						</Label>
						<Input
							id="categoryName"
							type="text"
							placeholder="Enter category name"
							value={categoryName}
							onChange={(e) => {
								setCategoryName(e.target.value);
								setErrors((prev) => ({ ...prev, categoryName: undefined }));
							}}
							invalid={!!errors.categoryName}
							max={31}
							maxLength={31}
						/>
						{errors.categoryName && (
							<FormFeedback>{errors.categoryName}</FormFeedback>
						)}
						{categoryName?.length >= 30 && (
							<span className="text-warning">
								Character limit met. Maximum 30 characters allowed
							</span>
						)}
					</FormGroup>

					<FormGroup>
						<Label
							for="mediaType"
							id="mediaTypeVideo"
							className="col-form-label"
						>
							Media Type
						</Label>
						<div className="d-flex">
							<div className="form-check form-check-inline d-flex align-items-center">
								<Input
									className="form-check-input"
									type="radio"
									id="mediaTypeVideo"
									name="mediaType"
									value="video"
									checked={mediaType === "video"}
									onChange={() => setMediaType("video")}
								/>
								<Label
									className="form-check-label m-0"
									htmlFor="mediaTypeVideo"
								>
									Video
								</Label>
							</div>
							<div className="form-check form-check-inline d-flex align-items-center">
								<Input
									className="form-check-input"
									type="radio"
									id="mediaTypeImage"
									name="mediaType"
									value="image"
									checked={mediaType === "image"}
									onChange={() => setMediaType("image")}
								/>
								<Label
									className="form-check-label m-0"
									htmlFor="mediaTypeImage"
								>
									Image
								</Label>
							</div>
						</div>
					</FormGroup>

					<FormGroup>
						<Label for="description" className="col-form-label">
							Description
						</Label>
						<Input
							id="description"
							type="text"
							placeholder="Enter description"
							value={description}
							onChange={(e) => {
								setDescription(e.target.value);
								setErrors((prev) => ({ ...prev, description: undefined }));
							}}
							max={101}
							maxLength={101}
						/>
						{description?.length >= 100 && (
							<span className="text-warning">
								Character limit met. Maximum 100 characters allowed
							</span>
						)}
					</FormGroup>

					<FormGroup>
						<Label for="categoryStatus" className="col-form-label">
							Category Status
						</Label>
						<div className="form-check form-switch">
							<Input
								type="checkbox"
								className="form-check-input"
								id="categoryStatus"
								checked={categoryStatus}
								onChange={() => setCategoryStatus(!categoryStatus)}
							/>
						</div>
					</FormGroup>
				</ModalBody>

				<ModalFooter>
					<Button
						type="button"
						color="secondary"
						disabled={isLoading}
						onClick={() => {
							toggleDetailModal();
							clearForm();
						}}
					>
						Cancel
					</Button>
					<Button type="submit" color="primary" disabled={isLoading}>
						{isLoading && <Spinner size="sm" />} {id > 0 ? "Update" : "Add"}
					</Button>
				</ModalFooter>
			</Form>
		</Modal>
	);
};

export default ManageCategoryModal;
